import { KeyboardEvent, useEffect, useState } from "react";
import { Box, Button, Dialog, IconButton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { IWhatsNewRelease, IWhatsNewSlide } from "./releases";

interface IWhatsNewModalProps {
  release: IWhatsNewRelease;
  open: boolean;
  onClose: () => void;
}

/** Selo com o caminho no menu até a tela da novidade. */
function LocationChip({ label }: { label: string }) {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        alignSelf: "flex-start",
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        color: theme.palette.accentText,
        backgroundColor: alpha(theme.palette.primary.dark, theme.palette.mode === "dark" ? 0.24 : 0.1),
      }}
    >
      {label}
    </Box>
  );
}

function SlideContent({ slide }: { slide: IWhatsNewSlide }) {
  const theme = useTheme();

  return (
    <Stack>
      {slide.image && (
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.action.hover,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            component="img"
            src={slide.image.src}
            alt={slide.image.alt}
            sx={{
              display: "block",
              width: "100%",
              height: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: "#fff",
              boxShadow: "0 4px 16px rgba(19, 21, 43, 0.10)",
            }}
          />
        </Box>
      )}

      <Stack spacing={1} sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {slide.location && <LocationChip label={slide.location} />}
        <Typography variant="h5" fontWeight={700}>
          {slide.title}
        </Typography>
        {slide.paragraphs?.map((paragraph) => (
          <Typography key={paragraph} variant="body1" sx={{ lineHeight: 1.55 }}>
            {paragraph}
          </Typography>
        ))}

        {slide.highlights && (
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {slide.highlights.map((highlight) => (
              <Stack
                key={highlight.title}
                spacing={0.5}
                sx={{ p: 1.75, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  {highlight.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {highlight.description}
                </Typography>
                {highlight.location && <LocationChip label={highlight.location} />}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

/** Carrossel com as novidades da versão; clicar fora não fecha, para ninguém perder as novidades sem querer. */
export default function WhatsNewModal({ release, open, onClose }: IWhatsNewModalProps) {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const total = release.slides.length;
  const isLast = index === total - 1;

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const goTo = (target: number) => setIndex(Math.max(0, Math.min(total - 1, target)));

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") goTo(index + 1);
    if (event.key === "ArrowLeft") goTo(index - 1);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      onKeyDown={handleKeyDown}
      maxWidth="md"
      fullWidth
      aria-labelledby="whats-new-title"
      PaperProps={{ sx: { borderRadius: 3, overflowX: "hidden", overflowY: "auto" } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pl: 3, pr: 1.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeOutlinedIcon fontSize="small" sx={{ color: "primary.dark" }} />
          <Typography id="whats-new-title" variant="subtitle1" fontWeight={500}>
            {release.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            v{release.version}
          </Typography>
        </Stack>
        <IconButton aria-label="Fechar" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* Os slides ficam lado a lado na mesma linha, então todos têm a altura do mais alto. */}
      <Box sx={{ flexShrink: 0, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            transform: `translateX(-${index * 100}%)`,
            transition: "transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1)",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          {release.slides.map((slide, slideIndex) => (
            <Box
              key={slide.title}
              role="group"
              aria-roledescription="slide"
              aria-label={`${slideIndex + 1} de ${total}`}
              aria-hidden={slideIndex !== index}
              sx={{ flex: "0 0 100%", minWidth: 0 }}
            >
              <SlideContent slide={slide} />
            </Box>
          ))}
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 1.75, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {release.slides.map((slide, slideIndex) => (
            <Box
              key={slide.title}
              component="button"
              type="button"
              aria-label={`Ir para o slide ${slideIndex + 1}`}
              aria-current={slideIndex === index}
              onClick={() => goTo(slideIndex)}
              sx={{
                p: 0,
                border: 0,
                cursor: "pointer",
                height: 8,
                width: slideIndex === index ? 22 : 8,
                borderRadius: 999,
                backgroundColor: slideIndex === index ? theme.palette.primary.dark : theme.palette.divider,
                transition: "width 0.2s, background-color 0.2s",
              }}
            />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1, display: { xs: "none", sm: "block" } }}>
            {index + 1} de {total}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button onClick={() => goTo(index - 1)} sx={{ visibility: index === 0 ? "hidden" : "visible" }}>
            Anterior
          </Button>
          <Button variant="contained" onClick={() => (isLast ? onClose() : goTo(index + 1))}>
            {isLast ? "Entendi" : "Próximo"}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
